import { MessageCircle, Play, ThumbsDown, ThumbsUp } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConfiguration } from '@/hooks/useConfiguration'
import { cn } from '@/lib/utils'

import type { TestCallFeedbackNote, TestCallFeedbackSentiment } from '../types'
import { formatDurationShort } from '../utils/callDetailFormat'

/** Node per sentiment, in the client Activity timeline's node style. */
const NODE_STYLE: Record<
  TestCallFeedbackSentiment | 'none',
  { node: string; Icon: typeof ThumbsUp; label: string | null }
> = {
  up: {
    node: 'bg-success-ui-bg border-success-subtle-border text-success-solid',
    Icon: ThumbsUp,
    label: 'Thumbs up',
  },
  down: {
    node: 'bg-error-ui-bg border-error-subtle-border text-error-solid',
    Icon: ThumbsDown,
    label: 'Thumbs down',
  },
  none: {
    node: 'bg-primary-ui-bg border-primary-subtle-border text-primary-solid',
    Icon: MessageCircle,
    label: null,
  },
}

/**
 * Notes left from the test call feedback bar, oldest first, on the client
 * Activity timeline's rail: "During call" notes seek the recording from their
 * timestamp, "After call" notes have none. Renders nothing unless the feature
 * is on and the call is a test call with notes.
 */
export function TestCallFeedbackTimeline({
  testCall,
  notes,
  onSeek,
}: {
  testCall: boolean
  notes: TestCallFeedbackNote[] | null | undefined
  /** Jumps the recording to a second; omit when there is no player. */
  onSeek?: (seconds: number) => void
}) {
  const { configuration } = useConfiguration()
  if (
    !configuration?.features?.test_call_feedback ||
    !testCall ||
    !notes?.length
  )
    return null

  const ordered = notes.toSorted((a, b) =>
    a.created_at.localeCompare(b.created_at)
  )
  const groups = [
    { label: 'During call', notes: ordered.filter((n) => n.phase === 'live') },
    { label: 'After call', notes: ordered.filter((n) => n.phase === 'ended') },
  ].filter((group) => group.notes.length)

  return (
    <section
      aria-label="Test call notes"
      className="mt-4 flex flex-col gap-4 border-t pt-3">
      {groups.map((group) => (
        <section key={group.label} aria-label={group.label}>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="text-muted-foreground text-xs font-medium tracking-wider whitespace-nowrap uppercase">
              {group.label}
            </span>
            <span aria-hidden className="bg-neutral-hover-bg h-px flex-1" />
            <Badge variant="secondary">{group.notes.length}</Badge>
          </div>
          <ul className="before:bg-neutral-active-bg relative flex flex-col gap-3.5 pl-[34px] before:absolute before:top-3 before:bottom-3 before:left-[11.5px] before:w-[1.5px] before:rounded-sm">
            {group.notes.map((note) => (
              <NoteRow key={note.id} note={note} onSeek={onSeek} />
            ))}
          </ul>
        </section>
      ))}
    </section>
  )
}

function NoteRow({
  note,
  onSeek,
}: {
  note: TestCallFeedbackNote
  onSeek?: (seconds: number) => void
}) {
  const style = NODE_STYLE[note.sentiment ?? 'none']
  const at = note.phase === 'live' ? note.at_seconds : null
  return (
    <li className="relative">
      <span
        aria-hidden
        className={cn(
          'absolute top-px -left-[34px] grid size-6 place-items-center rounded-full border shadow-[0_0_0_4px_var(--bg-app)] [&>svg]:size-3',
          style.node
        )}>
        <style.Icon />
      </span>
      <div className="group hover:bg-neutral-subtle-bg -mx-2 flex items-baseline justify-between gap-3 rounded-md px-2 pt-1 pb-1.5 transition-colors">
        <p className="text-text-body min-w-0 text-[13px] leading-[19px] break-words">
          {note.text ? (
            <>
              {style.label && <span className="sr-only">{style.label}: </span>}
              {note.text}
            </>
          ) : (
            <span className="text-neutral-solid italic">{style.label}</span>
          )}
        </p>
        {at !== null &&
          (onSeek ? (
            <Button
              type="button"
              variant="ghost"
              className="text-neutral-solid hover:bg-primary-ui-bg hover:text-primary-solid -my-0.5 -mr-1.5 h-auto shrink-0 gap-1 px-1.5 py-0.5 font-mono text-xs tabular-nums [&_svg]:size-2.5"
              aria-label={`Play from ${formatDurationShort(at)}`}
              onClick={() => onSeek(at)}>
              <Play
                aria-hidden
                className="fill-current opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
              />
              {formatDurationShort(at)}
            </Button>
          ) : (
            <span className="text-neutral-solid shrink-0 font-mono text-xs tabular-nums">
              {formatDurationShort(at)}
            </span>
          ))}
      </div>
    </li>
  )
}
